/**
 * ---------------------------------------------------------------------------------------------------------------------
 * RARIST: PRODUCT GALLERY ZOOM HELPER
 * ---------------------------------------------------------------------------------------------------------------------
 *
 * <product-zoom-helper> is rendered by snippets/product-gallery.liquid inside <product-gallery allow-zoom> (only when
 * image zoom is enabled in the section). It does not change the ProductGallery class from theme.js:
 *
 * 1. Click / tap on a product image opens the PhotoSwipe lightbox on that image. The lightbox is opened through the
 *    public "lightbox:open" event of <product-gallery> (the same one used by the "Zoom" button). Swiping the carousel
 *    never opens it: the pointer must not move more than TAP_TOLERANCE px and the carousel must not scroll between
 *    pointerdown and click. Videos and 3D models are ignored.
 *
 * 2. The "Zoom" button (plus icon + label) turns white when the image area under it is dark. A small copy of the
 *    image (SAMPLE_WIDTH px, crossOrigin="anonymous") is drawn in a canvas, and the mean relative luminance (WCAG) of
 *    the area under the button is compared with the button text color: the button becomes white when white has a
 *    better contrast than the default text color (for black text, this means a luminance below ~0.18, i.e. darker
 *    than #767676). If the measurement fails (CORS, tainted canvas, network error), the default look is kept.
 */

const TAP_TOLERANCE = 10; // Max pointer / carousel movement (in px) between pointerdown and click to be a "tap"
const SCROLL_GUARD = 150; // A tap within this delay (ms) after a carousel scroll only stops the scroll
const SAMPLE_WIDTH = 150; // Width (in px) of the image copy used to measure the luminance
const HYSTERESIS = 1.05; // Avoid flickering when the luminance is close to the threshold (e.g. during a swipe)
const MIN_COVERAGE = 0.5; // Minimum part of the button that must be over a measured image to take a decision
const LIGHT_CLASS = 'product-zoom-button--light';
const INDICATOR_LIGHT_CLASS = 'product-gallery__indicator--light'; // Rarist: lines / dots over the image
const INDICATOR_MIN_HEIGHT = 12; // Lines are only 1–2 px high: measure a band of at least this height around them

const finePointerMediaQuery = window.matchMedia('screen and (pointer: fine)');
const luminanceMaps = new Map(); // sample URL + background => { status: 'pending' | 'ready' | 'error', map, promise }

/* sRGB channel (0-255) to linear value lookup table */
const linearChannel = Array.from({ length: 256 }, (_, value) => {
  const channel = value / 255;
  return channel <= 0.04045 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
});

const contrastRatio = (luminanceA, luminanceB) => (Math.max(luminanceA, luminanceB) + 0.05) / (Math.min(luminanceA, luminanceB) + 0.05);

/**
 * Parse a computed CSS color ("rgb(r, g, b)", "rgba(r, g, b, a)" or "rgb(r g b / a)") into [r, g, b, a]
 */
function parseColor(color) {
  const parts = (color || '').match(/[\d.]+%?/g);

  if (!parts || parts.length < 3 || !/^rgba?\(/.test(color)) {
    return null;
  }

  const [red, green, blue] = parts.slice(0, 3).map((part) => parseFloat(part));
  const alpha = parts.length > 3 ? (parts[3].endsWith('%') ? parseFloat(parts[3]) / 100 : parseFloat(parts[3])) : 1;

  return [red, green, blue, alpha];
}

function relativeLuminance([red, green, blue]) {
  const index = (value) => Math.min(255, Math.max(0, Math.round(value)));
  return 0.2126 * linearChannel[index(red)] + 0.7152 * linearChannel[index(green)] + 0.0722 * linearChannel[index(blue)];
}

/**
 * First non-transparent background behind an element (used to flatten transparent PNG images)
 */
function backgroundColorOf(element) {
  for (let current = element; current && current.nodeType === Node.ELEMENT_NODE; current = current.parentElement) {
    const color = parseColor(getComputedStyle(current).backgroundColor);

    if (color && color[3] > 0) {
      return `rgb(${color[0]}, ${color[1]}, ${color[2]})`;
    }
  }

  return 'rgb(255, 255, 255)';
}

/**
 * Build the URL of a small copy of the image. Shopify CDN images accept a "width" parameter.
 */
function sampleUrlOf(image) {
  // Rarist: a video is measured through its poster (preview image), the only frame known in advance
  const source = image.tagName === 'VIDEO' ? image.getAttribute('poster') : (image.getAttribute('src') || image.currentSrc);

  if (!source || source.startsWith('data:') || source.startsWith('blob:')) {
    return null;
  }

  try {
    const url = new URL(source, window.location.href);
    url.searchParams.set('width', SAMPLE_WIDTH.toString());
    url.searchParams.delete('height');
    url.searchParams.delete('crop');
    return url.href;
  } catch (error) {
    return null;
  }
}

/**
 * Load the small copy of the image and turn it into a summed-area table of linear luminance, so that the mean
 * luminance of any rectangle can be computed in constant time (cheap enough to run during a swipe).
 */
function loadLuminanceMap(url, background) {
  const key = `${url}|${background}`;

  if (luminanceMaps.has(key)) {
    return luminanceMaps.get(key);
  }

  const entry = { status: 'pending', map: null };

  entry.promise = new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.decoding = 'async';

    image.onload = () => {
      try {
        const scale = Math.min(1, SAMPLE_WIDTH / image.naturalWidth),
          width = Math.max(1, Math.round(image.naturalWidth * scale)),
          height = Math.max(1, Math.round(image.naturalHeight * scale)),
          canvas = document.createElement('canvas');

        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext('2d', { willReadFrequently: true });
        context.fillStyle = background;
        context.fillRect(0, 0, width, height);
        context.drawImage(image, 0, 0, width, height);

        const pixels = context.getImageData(0, 0, width, height).data; // Throws a SecurityError if the canvas is tainted
        const table = new Float32Array((width + 1) * (height + 1));

        for (let y = 0; y < height; y++) {
          let rowSum = 0;

          for (let x = 0; x < width; x++) {
            const offset = (y * width + x) * 4;
            rowSum += 0.2126 * linearChannel[pixels[offset]] + 0.7152 * linearChannel[pixels[offset + 1]] + 0.0722 * linearChannel[pixels[offset + 2]];
            table[(y + 1) * (width + 1) + x + 1] = table[y * (width + 1) + x + 1] + rowSum;
          }
        }

        entry.status = 'ready';
        entry.map = { width, height, table };
      } catch (error) {
        entry.status = 'error';
      }

      resolve(entry);
    };

    image.onerror = () => {
      entry.status = 'error';
      resolve(entry);
    };

    image.src = url;
  });

  luminanceMaps.set(key, entry);
  return entry;
}

/**
 * Mean luminance of a rectangle (in sample image pixels, fractional coordinates are rounded to whole pixels)
 */
function meanLuminance(map, left, top, right, bottom) {
  let x0 = Math.max(0, Math.min(map.width - 1, Math.floor(left))),
    y0 = Math.max(0, Math.min(map.height - 1, Math.floor(top))),
    x1 = Math.max(x0 + 1, Math.min(map.width, Math.ceil(right))),
    y1 = Math.max(y0 + 1, Math.min(map.height, Math.ceil(bottom)));

  const stride = map.width + 1,
    sum = map.table[y1 * stride + x1] - map.table[y0 * stride + x1] - map.table[y1 * stride + x0] + map.table[y0 * stride + x0];

  return sum / ((x1 - x0) * (y1 - y0));
}

function parseObjectPosition(value, freeWidth, freeHeight) {
  const parts = (value || '50% 50%').trim().split(/\s+(?![^(]*\))/);

  return [parts[0], parts[1] ?? parts[0]].map((part, index) => {
    const free = index === 0 ? freeWidth : freeHeight;

    if (part.endsWith('%')) {
      return (free * parseFloat(part)) / 100;
    } else if (part.endsWith('px')) {
      return parseFloat(part);
    }

    return free / 2;
  });
}

function intersect(rectA, rectB) {
  const left = Math.max(rectA.left, rectB.left),
    top = Math.max(rectA.top, rectB.top),
    right = Math.min(rectA.right, rectB.right),
    bottom = Math.min(rectA.bottom, rectB.bottom);

  return right > left && bottom > top ? { left, top, right, bottom } : null;
}

/**
 * Mean luminance of the part of the image that is displayed inside "area" (viewport coordinates). It takes into
 * account object-fit / object-position, so cropped images are handled correctly.
 */
/**
 * The element whose pixels are measured for a gallery cell: the image, or (Rarist) the <video> of a Shopify video
 * with a poster. Other media (3D models, external videos) are not measurable.
 */
function measurableMediaOf(cell) {
  switch (cell?.getAttribute('data-media-type')) {
    case 'image':
      return cell.querySelector(':scope > img');

    case 'video':
      return cell.querySelector('video[poster]');

    default:
      return null;
  }
}

function imageAreaLuminance(image, map, area) {
  const box = image.getBoundingClientRect(),
    style = getComputedStyle(image),
    naturalScale = (image.naturalWidth || parseFloat(image.getAttribute('width')) || map.width) / map.width;

  let scaleX = box.width / map.width,
    scaleY = box.height / map.height;

  switch (style.objectFit) {
    case 'contain':
      scaleX = scaleY = Math.min(scaleX, scaleY);
      break;

    case 'cover':
      scaleX = scaleY = Math.max(scaleX, scaleY);
      break;

    case 'none':
      scaleX = scaleY = naturalScale;
      break;

    case 'scale-down':
      scaleX = scaleY = Math.min(scaleX, scaleY, naturalScale);
      break;
  }

  const [offsetX, offsetY] = parseObjectPosition(style.objectPosition, box.width - map.width * scaleX, box.height - map.height * scaleY),
    contentLeft = box.left + offsetX,
    contentTop = box.top + offsetY,
    visibleArea = intersect(area, box);

  if (!visibleArea) {
    return null;
  }

  return meanLuminance(
    map,
    (visibleArea.left - contentLeft) / scaleX,
    (visibleArea.top - contentTop) / scaleY,
    (visibleArea.right - contentLeft) / scaleX,
    (visibleArea.bottom - contentTop) / scaleY
  );
}

class ProductZoomHelper extends HTMLElement {
  connectedCallback() {
    this.gallery = this.closest('product-gallery');
    this.carousel = this.gallery?.querySelector('.product-gallery__carousel');

    if (!this.gallery || !this.carousel || !this.gallery.hasAttribute('allow-zoom')) {
      return;
    }

    this.abortController = new AbortController();
    this.hoveredButtons = new WeakSet();
    this.observedCells = new WeakSet();
    this.awaitedEntries = new WeakSet();
    this.lastCarouselScroll = 0;
    this.pointerStart = null;

    const listenerOptions = { signal: this.abortController.signal },
      passiveListenerOptions = { passive: true, signal: this.abortController.signal },
      scheduleUpdate = () => this.scheduleUpdate();

    // 1. Click on image (listeners on <product-gallery>, so they run after those of the carousel)
    if (!this.hasCursorNavigation) {
      this.gallery.classList.add('product-gallery--zoom-on-click');
    }

    this.gallery.addEventListener('pointerdown', this.onPointerDown.bind(this), passiveListenerOptions);
    this.gallery.addEventListener('click', this.onClick.bind(this), listenerOptions);

    // 2. Button color
    this.carousel.addEventListener('scroll', () => {
      this.lastCarouselScroll = performance.now();
      this.scheduleUpdate();
    }, passiveListenerOptions);

    this.carousel.addEventListener('carousel:change', scheduleUpdate, listenerOptions);
    this.carousel.addEventListener('carousel:settle', scheduleUpdate, listenerOptions);
    this.carousel.addEventListener('carousel:filter', scheduleUpdate, listenerOptions);
    document.forms[this.gallery.getAttribute('form')]?.addEventListener('variant:change', scheduleUpdate, listenerOptions);
    finePointerMediaQuery.addEventListener('change', scheduleUpdate, listenerOptions);

    this.zoomButtons.forEach((button) => {
      button.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') {
          this.hoveredButtons.add(button);
          this.scheduleUpdate();
        }
      }, listenerOptions);

      button.addEventListener('pointerleave', () => {
        this.hoveredButtons.delete(button);
        this.scheduleUpdate();
      }, listenerOptions);
    });

    // Buttons placed on each image (grid layout) are only measured once their image gets close to the viewport
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.observedCells.add(entry.target);
          this.intersectionObserver.unobserve(entry.target);
        }
      });

      this.scheduleUpdate();
    }, { rootMargin: '300px 0px' });

    this.zoomButtons.forEach((button) => {
      const cell = button.closest('.product-gallery__media');

      if (cell) {
        this.intersectionObserver.observe(cell);
      }
    });

    this.resizeObserver = new ResizeObserver(scheduleUpdate);
    this.resizeObserver.observe(this.gallery);

    this.scheduleUpdate();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.intersectionObserver?.disconnect();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.frame);
    this.frame = null;
    this.gallery?.classList.remove('product-gallery--zoom-on-click');
  }

  get cells() {
    return Array.from(this.carousel.children).filter((cell) => cell.classList.contains('product-gallery__media') && !cell.hasAttribute('hidden'));
  }

  get zoomButtons() {
    return Array.from(this.gallery.querySelectorAll('.product-zoom-button'));
  }

  /**
   * Rarist: the "lines" / "dots" indicators floating over the image (instead of thumbnails) turn white on a dark image
   * too, exactly like the zoom button.
   */
  get indicators() {
    return Array.from(this.gallery.querySelectorAll('.product-gallery__controls-floating :is(.scroll-marker-group--lines, .scroll-marker-group--dots)'));
  }

  /**
   * In "carousel" desktop layout, ProductGallery uses a click on the image to go to the next / previous image (with a
   * custom arrow cursor). In that case, we do not change the click behavior on devices with a fine pointer.
   */
  get hasCursorNavigation() {
    return this.gallery.querySelector('.product-gallery__cursor') !== null;
  }

  /**
   * -------------------------------------------------------------------------------------------------------------------
   * CLICK ON IMAGE
   * -------------------------------------------------------------------------------------------------------------------
   */

  onPointerDown(event) {
    if (!event.isPrimary) {
      return;
    }

    this.pointerStart = {
      x: event.clientX,
      y: event.clientY,
      scrollLeft: this.carousel.scrollLeft,
      scrollTop: this.carousel.scrollTop,
      stopsScroll: performance.now() - this.lastCarouselScroll < SCROLL_GUARD
    };
  }

  onClick(event) {
    const pointerStart = this.pointerStart;
    this.pointerStart = null;

    if (event.defaultPrevented || event.button !== 0 || !(event.target instanceof Element)) {
      return;
    }

    const image = event.target.closest('img'),
      cell = image?.parentElement;

    if (!cell || cell.parentElement !== this.carousel || cell.getAttribute('data-media-type') !== 'image' || cell.hasAttribute('hidden')) {
      return;
    }

    if (this.hasCursorNavigation && finePointerMediaQuery.matches) {
      return;
    }

    if (pointerStart) {
      const pointerDistance = Math.hypot(event.clientX - pointerStart.x, event.clientY - pointerStart.y),
        scrollDistance = Math.max(Math.abs(this.carousel.scrollLeft - pointerStart.scrollLeft), Math.abs(this.carousel.scrollTop - pointerStart.scrollTop));

      if (pointerStart.stopsScroll || pointerDistance > TAP_TOLERANCE || scrollDistance > TAP_TOLERANCE) {
        return;
      }
    }

    // Same image list (and therefore same index) as ProductGallery.openLightBox
    const images = this.cells.flatMap((item) => Array.from(item.querySelectorAll(':scope > img'))),
      index = images.indexOf(image);

    if (index !== -1) {
      this.gallery.dispatchEvent(new CustomEvent('lightbox:open', { bubbles: true, detail: { index } }));
    }
  }

  /**
   * -------------------------------------------------------------------------------------------------------------------
   * BUTTON COLOR
   * -------------------------------------------------------------------------------------------------------------------
   */

  scheduleUpdate() {
    if (this.frame) {
      return;
    }

    this.frame = requestAnimationFrame(() => {
      this.frame = null;

      if (this.isConnected) {
        this.update();
      }
    });
  }

  update() {
    const selectedCell = this.carousel.selectedCell;

    this.zoomButtons.forEach((button) => {
      const ownCell = button.closest('.product-gallery__media');

      if (ownCell) {
        if (this.observedCells.has(ownCell)) {
          this.updateButton(button, [ownCell]);
        }
      } else {
        // With grid desktop layouts, ProductGallery hides the first zoom button of the gallery (the one of the first
        // image) instead of this shared button when the selected media is a video or a 3D model. We fix it here.
        if (selectedCell && this.gallery.querySelector('.product-gallery__media .product-zoom-button')) {
          button.parentElement.toggleAttribute('hidden', selectedCell.getAttribute('data-media-type') !== 'image');
        }

        this.updateButton(button, this.cells);

        // Measure the images next to the selected one in advance, so that the color is right as soon as they slide in
        if (button.getClientRects().length > 0) {
          const cells = this.cells,
            selectedIndex = cells.indexOf(selectedCell);

          [cells[selectedIndex - 1], cells[selectedIndex + 1]].forEach((cell) => {
            const image = measurableMediaOf(cell),
              url = image ? sampleUrlOf(image) : null;

            if (url) {
              loadLuminanceMap(url, backgroundColorOf(cell));
            }
          });
        }
      }
    });

    this.indicators.forEach((indicator) => {
      this.updateContrast(indicator, this.cells, this.indicatorArea(indicator), INDICATOR_LIGHT_CLASS, indicator);
    });
  }

  /**
   * The area covered by the markers themselves (the group spans half of the gallery width, the markers only its center).
   * Lines are very thin, so the measured band is at least INDICATOR_MIN_HEIGHT px high.
   */
  indicatorArea(indicator) {
    const rects = Array.from(indicator.querySelectorAll('.scroll-marker:not([hidden])'), (marker) => marker.getBoundingClientRect()).filter((rect) => rect.width > 0);

    if (rects.length === 0) {
      return indicator.getBoundingClientRect();
    }

    const left = Math.min(...rects.map((rect) => rect.left)),
      right = Math.max(...rects.map((rect) => rect.right)),
      top = Math.min(...rects.map((rect) => rect.top)),
      bottom = Math.max(...rects.map((rect) => rect.bottom)),
      extra = Math.max(0, INDICATOR_MIN_HEIGHT - (bottom - top)) / 2;

    return new DOMRect(left, top - extra, right - left, bottom - top + extra * 2);
  }

  /**
   * The area covered by the button. On devices with a fine pointer, the label expands on hover (CSS width transition
   * on the label, the end of the button being fixed), so we directly use its final size to avoid any flash.
   */
  buttonArea(button) {
    const rect = button.getBoundingClientRect();

    if (!this.hoveredButtons.has(button) || !finePointerMediaQuery.matches) {
      return rect;
    }

    const label = button.querySelector('.product-zoom-button__label'),
      icon = button.querySelector('svg'),
      width = (icon ? icon.getBoundingClientRect().width : 0) + (label ? label.scrollWidth : 0);

    if (width <= rect.width) {
      return rect;
    }

    const isRtl = getComputedStyle(button).direction === 'rtl';
    return new DOMRect(isRtl ? rect.left : rect.right - width, rect.top, width, rect.height);
  }

  updateButton(button, cells) {
    if (button.getClientRects().length === 0) {
      return; // Button not displayed (hidden attribute, sm:hidden...)
    }

    this.updateContrast(button, cells, this.buttonArea(button), LIGHT_CLASS, button.parentElement);
  }

  /**
   * Toggle lightClass on element when white has a better contrast than its text color (read on colorSource) with the
   * images under area. Shared by the zoom buttons and (Rarist) the lines / dots indicators.
   */
  updateContrast(element, cells, area, lightClass, colorSource) {
    if (element.getClientRects().length === 0) {
      return; // Not displayed (hidden attribute, md:hidden...)
    }

    const buttonSurface = area.width * area.height;

    let luminanceSum = 0,
      coveredSurface = 0,
      isPending = false;

    cells.forEach((cell) => {
      const cellRect = cell.getBoundingClientRect(),
        cellArea = intersect(area, cellRect);

      if (!cellArea) {
        return;
      }

      const image = measurableMediaOf(cell),
        url = image ? sampleUrlOf(image) : null;

      if (!url) {
        return;
      }

      const entry = loadLuminanceMap(url, backgroundColorOf(cell));

      if (entry.status === 'pending') {
        isPending = true;

        if (!this.awaitedEntries.has(entry)) {
          this.awaitedEntries.add(entry);
          entry.promise.then(() => this.scheduleUpdate());
        }

        return;
      }

      if (entry.status !== 'ready') {
        return;
      }

      const luminance = imageAreaLuminance(image, entry.map, cellArea);

      if (luminance !== null) {
        const surface = (cellArea.right - cellArea.left) * (cellArea.bottom - cellArea.top);
        luminanceSum += luminance * surface;
        coveredSurface += surface;
      }
    });

    if (isPending) {
      return; // Keep the current state until the image has been measured
    }

    if (buttonSurface === 0 || coveredSurface / buttonSurface < MIN_COVERAGE) {
      element.classList.remove(lightClass); // Not over a (measurable) image: default look
      return;
    }

    // The text color is read on colorSource (the parent of the button, the indicator itself), which never gets the
    // light class, so the comparison always uses the default color
    const backgroundLuminance = luminanceSum / coveredSurface,
      textColor = parseColor(getComputedStyle(colorSource === element ? element.parentElement : colorSource).color),
      textLuminance = textColor ? relativeLuminance(textColor) : 0,
      whiteContrast = contrastRatio(1, backgroundLuminance),
      textContrast = contrastRatio(textLuminance, backgroundLuminance),
      isLight = element.classList.contains(lightClass);

    element.classList.toggle(lightClass, isLight ? whiteContrast * HYSTERESIS > textContrast : whiteContrast > textContrast * HYSTERESIS);
  }
}

if (!window.customElements.get('product-zoom-helper')) {
  window.customElements.define('product-zoom-helper', ProductZoomHelper);
}
